import { InteractiveStory, StoryLyricSection } from "./storyTypes";

type SrtCue = {
  start: number;
  end: number;
  text: string;
};

const lyricCues: SrtCue[] = [
  { start: 0.966, end: 2.066, text: "(Are you ready?)" },
  { start: 2.066, end: 2.933, text: "Yes, we are!" },
  { start: 2.933, end: 4.0, text: "(Can we do it?)" },
  { start: 4.0, end: 4.866, text: "Yes, we can!" },
  { start: 4.9, end: 8.0, text: "One, two, three, four, here we go!" },
  { start: 8.3, end: 10.566, text: "Step by step, watch us grow!" },
  { start: 16.166, end: 17.933, text: "The lift is stuck" },
  { start: 17.933, end: 19.633, text: "the bags are heavy" },
  { start: 20.133, end: 23.566, text: "But we have a plan, we are ready!" },
  { start: 24.033, end: 27.2, text: "Mei takes the left, Leo the right" },
  { start: 27.8, end: 30.633, text: "We carry the load with all our might!" },
  { start: 31.033, end: 35.033, text: "Up the stairs, one by one" },
  { start: 35.9, end: 39.0, text: "Getting work nicely done!" },
  { start: 39.8, end: 42.9, text: "Pass the bag, help a friend" },
  { start: 43.7, end: 46.133, text: "Working 'til the very end!" },
  { start: 48.166, end: 51.633, text: "Lok's on the stairs with a big smile" },
  { start: 52.133, end: 55.2, text: "We've been climbing for quite a while" },
  { start: 56.0, end: 59.533, text: "Count the floors and pass them fast" },
  { start: 60.0, end: 63.166, text: "This hard work is not gonna last!" },
  { start: 63.166, end: 67.0, text: "Up the stairs, one by one" },
  { start: 67.833, end: 70.966, text: "Getting work nicely done!" },
  { start: 71.766, end: 74.833, text: "Pass the bag, help a friend" },
  { start: 75.7, end: 78.133, text: "Working 'til the very end!" },
  { start: 105.2, end: 113.4, text: "We made it to the very top!" },
  { start: 113.4, end: 115.533, text: "Our helping hands will never stop!" },
  { start: 115.533, end: 117.433, text: "(Yay!) Go team, go!" },
  { start: 117.533, end: 121.8, text: "Together is the way to grow!" },
];

const normalizeLyric = (text: string) =>
  text
    .toLowerCase()
    .replace(/^(intro|chorus|outro|verse\s*\d+)\s+/i, "")
    .replace(/[^\w\s']/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const withLyricTimes = (
  sections: { heading: string; lines: string[] }[]
): StoryLyricSection[] => {
  let cueIndex = 0;

  return sections.map((section) => ({
    heading: section.heading,
    lines: section.lines.map((text) => {
      const normLine = normalizeLyric(text);
      let start: number | undefined;
      let end: number | undefined;

      while (cueIndex < lyricCues.length) {
        const cue = lyricCues[cueIndex];
        const normCue = normalizeLyric(cue.text);

        if (!normCue || (!normLine.includes(normCue) && !normCue.includes(normLine))) {
          break;
        }

        start = start ?? cue.start;
        end = cue.end;
        cueIndex += 1;
      }

      return { text, start, end };
    }),
  }));
};

const storyFile = (fileName: string) =>
  `https://www.pkps.edu.hk/eng/Ebook2/${fileName}`;

export const greatStaircaseRelayStory: InteractiveStory = {
  id: "great-staircase-relay",
  title: "The Great Staircase Relay",
  startSceneId: "cover",
  scenes: [
    {
      id: "cover",
      layout: "cover",
      image: storyFile("cover.jpeg"),
    },
    {
      id: "page-1",
      layout: "spread",
      image: storyFile("page3.jpeg"),
      clips: [storyFile("page1-1.m4a"), storyFile("page1-2.m4a")],
      text: `Leo came home after school. He lived in a tall building in Hong Kong. Oh no! The lift was broken.

Grandma stood in the lobby. She held two heavy bags of vegetables. "The bags are too heavy," said Grandma. "I cannot walk up the stairs!"`,
    },
    {
      id: "page-2",
      layout: "spread",
      image: storyFile("page2.jpeg"),
      clips: [storyFile("page2-1.m4a"), storyFile("page2-2.m4a")],
      text: `Leo wanted to help. He called his good friends, Abby and Tim. "Let's work together!" said Leo.

Leo, Abby, and Tim stood on different floors on the stairs. They passed the bags from hand to hand. Up, up, up went the bags!`,
    },
    {
      id: "page-3",
      layout: "spread",
      image: storyFile("page1.jpeg"),
      clips: [storyFile("page3-1.m4a"), storyFile("page3-2.m4a")],
      text: `There was a big watermelon. It was very heavy! Tim could not lift it alone. "Let me help you!" said Leo.

"One, two, three, lift!" they shouted. Together, Leo and Tim carried the heavy watermelon up the stairs.`,
    },
    {
      id: "page-4",
      layout: "spread",
      image: storyFile("page4.jpeg"),
      clips: [storyFile("page4-1.m4a"), storyFile("page4-2.m4a")],
      text: `Soon, all the bags were outside the apartment doors. Grandma and the neighbours were very happy. They clapped for the kids.

Grandma gave Leo, Abby, and Tim cold lemon tea to drink. Leo smiled. "Working together makes hard work easier!" he said.`,
    },
    {
      id: "song",
      layout: "lyrics",
      heading: "Up the Stairs Together",
      image: storyFile("cover.jpeg"),
      audio: storyFile("song.mp3"),
      text: "Listen and sing the song with your parents.",
      lyrics: withLyricTimes([
        {
          heading: "Intro",
          lines: [
            "(Are you ready?)",
            "Yes, we are!",
            "(Can we do it?)",
            "Yes, we can!",
            "One, two, three, four, here we go!",
            "Step by step, watch us grow!",
          ],
        },
        {
          heading: "Verse 1",
          lines: [
            "The lift is stuck, the bags are heavy,",
            "But we have a plan, we are ready!",
            "Mei takes the left, Leo the right,",
            "We carry the load with all our might!",
          ],
        },
        {
          heading: "Chorus",
          lines: [
            "Up the stairs, one by one,",
            "Getting work nicely done!",
            "Pass the bag, help a friend,",
            "Working 'til the very end!",
          ],
        },
        {
          heading: "Verse 2",
          lines: [
            "Lok's on the stairs with a big smile,",
            "We've been climbing for quite a while.",
            "Count the floors and pass them fast,",
            "This hard work is not gonna last!",
          ],
        },
        {
          heading: "Chorus",
          lines: [
            "Up the stairs, one by one,",
            "Getting work nicely done!",
            "Pass the bag, help a friend,",
            "Working 'til the very end!",
          ],
        },
        {
          heading: "Outro",
          lines: [
            "We made it to the very top!",
            "Our helping hands will never stop!",
            "(Yay!) Go team, go!",
            "Together is the way to grow!",
          ],
        },
      ]),
    },
  ],
};
